import { ContactType } from '@/core/domain/_shared/ContactType';
import flatContacts from '@/utils/functions/flatContacts';
import { applyCpfCnpjMask, applyMask, removeMask } from '@/utils/mask';
import { cellphoneMask, cepMask, phoneMask } from '@/utils/mask/commonMasks';
import {
  Descriptions,
  Tooltip,
  Badge,
  Dropdown,
  Menu,
  Popover,
  Spin,
  Button,
  Modal,
} from 'antd';
import { useIntl } from 'react-intl';
import { useWorkorderDetailContext } from '../WorkorderDetailProvider';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useMemo } from 'react';
import { useCustomerAddresses } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrixCustomerSettingsDetails } from '@/core/domain/enterprise/MatrixCustomerSettings/MatrixCustomerSettings.hooks';
import SkeletonCustom from '@/components/_common/SkeletonCustom';
import { DeleteIcon, Group } from '@/components/_common';
import { Edit, Plus } from 'react-feather';
import { CustomerAddress } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import ActionIcon from '@/components/_common/ActionIcon';
import { useWorkOrderCustomerAddressUpdate } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';

export default function CustomerDetailsDescriptions() {
  const intl = useIntl();

  const {
    workorder: { data: workOrder, isFetching: workOrderIsFetching },
  } = useWorkorderDetailContext();

  const { data: customerMatrixSettings } = useMatrixCustomerSettingsDetails();

  const customer = workOrder?.customer;

  const customerContacts = useCustomerContacts(customer?.id);

  const phoneNumberMain = useMemo(() => {
    return flatContacts(customerContacts.data).find(
      (contact) =>
        contact.contactType === ContactType.Phone ||
        contact.contactType === ContactType.Mobile,
    )?.value;
  }, [customerContacts.data]);

  const customerAddresses = useCustomerAddresses(customer?.id);
  const customerAddressUpdate = useWorkOrderCustomerAddressUpdate();

  const formatAddress = (address?: CustomerAddress) => {
    if (!address) return '-';

    const zipcode = applyMask(removeMask(address.zipCode), cepMask);

    return `${address?.street}, ${address?.number}, ${
      address.complement ? `${address.complement}, ` : ''
    } ${address?.district} - ${address?.city || '-'}/${
      address?.state || '-'
    } ${zipcode}`.trim();
  };

  const customerAddressSelected = useMemo(() => {
    const addresses = customerAddresses?.data?.data ?? [];

    if (!addresses.length) return { address: undefined, description: '-' };

    const selectedAddress = addresses.find(
      ({ id }) => id === workOrder?.customerAddressId,
    );

    return {
      address: selectedAddress,
      description: formatAddress(selectedAddress),
    };
  }, [customerAddresses, workOrder?.customerAddressId]);

  const addressOptions = customerAddresses.data?.data
    .filter((address) => address.id !== customerAddressSelected.address?.id)
    .map((address) => ({
      key: address.id,
      label: formatAddress(address),
    }));

  const handleDeleteAddress = (address: CustomerAddress) => {
    Modal.confirm({
      title: 'Remover endereço do orçamento',
      content: (
        <>
          Deseja remover este endereço?
          <br />
          <br />
          <strong>{formatAddress(address)}</strong>
        </>
      ),

      onOk: async () => {
        if (workOrder?.id) {
          await customerAddressUpdate.mutateAsync({
            workOrderId: workOrder.id,
            customerAddressId: undefined,
          });
        }
      },
      okText: 'Remover',
    });
  };

  const renderDocumentValue = () => {
    if (!customer) return;

    if (customer.foreignDocument) {
      return customer.foreignDocument;
    }
    if (customer.cpfCnpj) {
      return applyCpfCnpjMask(customer.cpfCnpj);
    }

    return '-';
  };

  return (
    <SkeletonCustom
      skeletonType="INPUT"
      loading={customerContacts.isLoading || customerAddresses.isLoading}
      skeletonCount={1}
      style={{ minHeight: 150 }}
    >
      <Descriptions
        bordered
        column={{ xs: 8, md: 12, xl: 24 }}
        layout="vertical"
        className="workorder-descriptions"
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Descriptions.Item
          span={8}
          label={intl.formatMessage({
            id: 'workOrders.domain.customer',
          })}
        >
          {customer?.name || '-'}
        </Descriptions.Item>

        {!!customer?.category?.id &&
          customerMatrixSettings?.showCategoryInWorkOrder && (
            <Descriptions.Item span={8} label="Categoria">
              <StatusTag
                color={customer?.category.color}
                label={customer?.category.name}
                title={customer?.category.name}
                size="md"
                style={{ width: 400, display: 'block' }}
              />
            </Descriptions.Item>
          )}

        <Descriptions.Item
          span={8}
          label={
            !!customer?.foreignDocument
              ? 'Doc. identificação estrangeiro'
              : intl.formatMessage({
                  id: 'customers.details.generalInfo.form.cpfCnpj',
                })
          }
        >
          {renderDocumentValue()}
        </Descriptions.Item>

        <Descriptions.Item
          span={8}
          label={intl.formatMessage({
            id: 'commons.contact.contactType.email',
          })}
        >
          {flatContacts(customerContacts.data).find(
            (contact) => contact.contactType === ContactType.Email,
          )?.value || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          span={8}
          label={intl.formatMessage({
            id: 'commons.contact.contactType.phone',
          })}
        >
          {phoneNumberMain
            ? applyMask(removeMask(phoneNumberMain), cellphoneMask)
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          span={8}
          label={intl.formatMessage({ id: 'commons.address' })}
        >
          <Group gap={10}>
            {addressOptions?.length ? (
              <Dropdown
                placement="bottom"
                trigger={['click']}
                overlay={
                  <Menu
                    disabled={
                      customerAddressUpdate.isLoading || workOrderIsFetching
                    }
                    items={[
                      ...addressOptions,
                      ...(customerAddressSelected.address
                        ? [
                            {
                              key: 'remove',
                              label: 'Remover endereço',
                              danger: true,
                            },
                          ]
                        : []),
                    ]}
                    onClick={async ({ key }) => {
                      if (!workOrder?.id) return;

                      if (key === 'remove' && customerAddressSelected.address) {
                        return handleDeleteAddress(
                          customerAddressSelected.address,
                        );
                      }

                      await customerAddressUpdate.mutateAsync({
                        workOrderId: workOrder.id,
                        customerAddressId: Number(key),
                      });
                    }}
                  />
                }
              >
                <ActionIcon>
                  <Group gap={10}>
                    {!!customerAddressSelected.address &&
                      customerAddressSelected.description}

                    {customerAddressUpdate.isLoading || workOrderIsFetching ? (
                      <Spin size="small" />
                    ) : (
                      <Popover
                        content={`${
                          customerAddressSelected.address
                            ? 'Trocar'
                            : 'Adicionar'
                        } Endereço`}
                      >
                        {!!customerAddressSelected.address ? (
                          <Edit size={16} />
                        ) : (
                          <Button
                            type="primary"
                            size="small"
                            icon={<AntdFeatherIcon icon={<Plus />} size={16} />}
                            shape="round"
                          />
                        )}
                      </Popover>
                    )}
                  </Group>
                </ActionIcon>
              </Dropdown>
            ) : (
              <>
                {customerAddressSelected.description}

                {!!customerAddressSelected.address && (
                  <DeleteIcon
                    iconProps={{ size: 16 }}
                    onClick={() => {
                      if (customerAddressSelected.address) {
                        handleDeleteAddress(customerAddressSelected.address);
                      }
                    }}
                  />
                )}
              </>
            )}
          </Group>
        </Descriptions.Item>

        <Descriptions.Item
          span={8}
          label={`${intl.formatMessage({
            id: 'commons.others',
          })} ${intl.formatMessage({ id: 'commons.contacts' })}`}
        >
          {(() => {
            const contacts = flatContacts(customerContacts.data).filter(
              (contact) => contact.contactType !== ContactType.Email,
            );

            if (!contacts?.length) return '-';

            const maskedContacts = contacts.map(({ value: phone }) => {
              const phoneNumber = removeMask(phone);
              return phoneNumber.length <= 10
                ? applyMask(phoneNumber, phoneMask)
                : applyMask(phoneNumber, cellphoneMask);
            });

            return (
              <>
                {maskedContacts[0]}{' '}
                {maskedContacts.length > 1 && (
                  <Tooltip title={maskedContacts.slice(1).join(', ')}>
                    <Badge
                      count={`+${maskedContacts.slice(1).length}`}
                      style={{
                        backgroundColor: '#364068',
                      }}
                    />
                  </Tooltip>
                )}
              </>
            );
          })()}
        </Descriptions.Item>
      </Descriptions>
    </SkeletonCustom>
  );
}
